import { useState } from 'react';
import { ChevronDown, ChevronRight, Sparkles, AlertTriangle } from 'lucide-react';
import CampoNumero from './CampoNumero';
import { EtiquetaVinculo } from './VinculoErp';
import { formatarMoeda } from './formato';

// Campos de valor editáveis do item (todos vêm do XML; o usuário confere e corrige se precisar).
const CAMPOS_VALORES = [
  { chave: 'quantidade', label: 'Quantidade (qCom)' },
  { chave: 'valorUnitario', label: 'Valor unitário (vUnCom)' },
  { chave: 'vProd', label: 'Valor do produto (vProd)' },
  { chave: 'vDesc', label: 'Desconto (vDesc)' },
  { chave: 'vFrete', label: 'Frete na nota (vFrete)' },
  { chave: 'vSeg', label: 'Seguro (vSeg)' },
  { chave: 'vOutro', label: 'Outras despesas (vOutro)' },
];

const CAMPOS_IMPOSTOS = [
  { chave: 'pIPI', label: 'IPI %' },
  { chave: 'vIPI', label: 'IPI R$' },
  { chave: 'vBC', label: 'Base ICMS' },
  { chave: 'pICMS', label: 'ICMS % (interest.)' },
  { chave: 'vICMS', label: 'ICMS R$' },
  { chave: 'vBCST', label: 'Base ST' },
  { chave: 'pMVAST', label: 'MVA ST %' },
  { chave: 'vICMSST', label: 'ICMS-ST R$' },
  { chave: 'vFCPST', label: 'FCP-ST R$' },
  { chave: 'vPIS', label: 'PIS R$' },
  { chave: 'vCOFINS', label: 'COFINS R$' },
];

function Identificacao({ item }) {
  const campos = [
    ['Código', item.codigo], ['EAN', item.ean || 'sem GTIN'], ['NCM', item.ncm], ['CEST', item.cest || '—'],
    ['CFOP', item.cfop], ['Origem', item.orig || '—'], ['CST/CSOSN', item.cst || '—'], ['Unidade', item.unidade],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
      {campos.map(([rotulo, valor]) => (
        <div key={rotulo}>
          <p className="text-[11px] text-gray-500">{rotulo}</p>
          <p className="text-sm font-mono text-gray-900 truncate" title={valor}>{valor || '—'}</p>
        </div>
      ))}
    </div>
  );
}

function ItemNota({ item, aberto, onAlternar, onAlterar }) {
  const sugestao = item.iaSugestao;
  const temAlerta = Boolean(sugestao?.alerta_unidade);
  const campo = (chave, valor) => onAlterar(item.indice, chave, valor);

  return (
    <div className={`border rounded-xl bg-white ${temAlerta ? 'border-amber-300' : 'border-gray-200'}`}>
      <button
        type="button"
        onClick={onAlternar}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 rounded-xl"
      >
        {aberto ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        <span className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700 flex-shrink-0">
          {item.indice + 1}
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 truncate">{item.descricao}</p>
          <p className="text-xs text-gray-500">
            {item.quantidade} {item.unidade} × {formatarMoeda(item.valorUnitario)} = {formatarMoeda(item.vProd)}
            {Number(item.fatorConversao) !== 1 && <> · {item.fatorConversao} peça(s) por {item.unidade}</>}
          </p>
        </div>
        <EtiquetaVinculo item={item} />
        <span className={`px-2 py-0.5 rounded text-xs font-bold ${item.tributacao === 'st' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
          {item.tributacao === 'st' ? 'ST' : 'Normal'}
        </span>
        {item.calcularAntecipacao && (
          <span className="px-2 py-0.5 rounded text-xs font-bold bg-orange-100 text-orange-700">Antecipação</span>
        )}
        {temAlerta && (
          <span title={sugestao.alerta_unidade}><AlertTriangle className="w-4 h-4 text-amber-500" /></span>
        )}
      </button>

      {aberto && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-3">
          <Identificacao item={item} />

          <div>
            <p className="text-xs font-semibold text-gray-700 mb-1">Valores do item</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {CAMPOS_VALORES.map(c => (
                <CampoNumero key={c.chave} label={c.label} valor={item[c.chave]} onChange={v => campo(c.chave, v)} minimo={0} />
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-700 mb-1">Impostos destacados na nota</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-11 gap-2">
              {CAMPOS_IMPOSTOS.map(c => (
                <CampoNumero key={c.chave} label={c.label} valor={item[c.chave]} onChange={v => campo(c.chave, v)} minimo={0} />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 rounded-lg bg-blue-50/60 border border-blue-100">
            <CampoNumero
              label={`Peças por ${item.unidade || 'unidade'} (fator)`}
              valor={item.fatorConversao}
              onChange={v => campo('fatorConversao', v)}
              minimo={0}
              destaque
              titulo="Ex.: nota em CX com 12 peças e a loja vende por peça → 12"
            />
            <label className="block">
              <span className="block text-[11px] font-medium text-gray-500 mb-0.5">Tributação do ICMS</span>
              <select
                value={item.tributacao}
                onChange={e => campo('tributacao', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
              >
                <option value="normal">Normal (gera crédito no Presumido)</option>
                <option value="st">Substituição tributária (ST)</option>
              </select>
            </label>
            <label className="flex items-end gap-2 pb-1">
              <input
                type="checkbox"
                checked={item.calcularAntecipacao}
                onChange={e => campo('calcularAntecipacao', e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-700">Calcular antecipação / DIFAL no CE</span>
            </label>
            <CampoNumero
              label="MVA da antecipação % (0 = só diferença de alíquota)"
              valor={item.mvaAntecipacao}
              onChange={v => campo('mvaAntecipacao', v)}
              minimo={0}
              desabilitado={!item.calcularAntecipacao}
            />
          </div>

          {sugestao && (
            <div className="flex items-start gap-2 text-xs text-gray-700 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
              <Sparkles className="w-4 h-4 text-violet-500 flex-shrink-0" />
              <div className="space-y-0.5">
                <p>
                  <strong>Sugestão da IA:</strong>{' '}
                  {sugestao.sujeito_st_ce ? 'sujeito a ST no CE' : 'sem ST no CE'}
                  {' · '}{sugestao.sujeito_antecipacao_ce ? 'com antecipação' : 'sem antecipação'}
                  {' · '}alíquota interestadual esperada {sugestao.aliquota_interestadual_esperada}%
                  {Number(item.pICMS) > 0 && Number(item.pICMS) !== sugestao.aliquota_interestadual_esperada && (
                    <span className="text-red-600 font-semibold"> (a nota usou {item.pICMS}%, confira)</span>
                  )}
                </p>
                {sugestao.alerta_unidade && <p className="text-amber-700"><strong>Unidade:</strong> {sugestao.alerta_unidade}</p>}
                {sugestao.observacao && <p>{sugestao.observacao}</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Lista de itens da NF-e, cada um expansível com todos os campos editáveis para conferência. */
export default function ItensNota({ itens, onAlterar }) {
  const [abertos, setAbertos] = useState(() => new Set(itens.length <= 3 ? itens.map(i => i.indice) : []));
  const todosAbertos = abertos.size === itens.length;

  const alternar = (indice) => setAbertos(prev => {
    const novo = new Set(prev);
    if (novo.has(indice)) novo.delete(indice); else novo.add(indice);
    return novo;
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Itens da nota ({itens.length})</h3>
        <button
          type="button"
          onClick={() => setAbertos(todosAbertos ? new Set() : new Set(itens.map(i => i.indice)))}
          className="text-xs text-blue-600 hover:underline"
        >
          {todosAbertos ? 'Recolher todos' : 'Expandir todos'}
        </button>
      </div>
      {itens.map(item => (
        <ItemNota
          key={item.indice}
          item={item}
          aberto={abertos.has(item.indice)}
          onAlternar={() => alternar(item.indice)}
          onAlterar={onAlterar}
        />
      ))}
    </div>
  );
}
